/**
 * APIクライアント
 * バックエンドAPIとの通信を管理
 */

import { getAppConfig } from '../config';

const config = getAppConfig();
const API_BASE_URL = config.apiBaseUrl;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: Response,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * サーバーが返したエラー本文を取り出す。
 *
 * `ApiError` の message は「API request failed: <status>」で、利用者に見せたい本文は
 * レスポンスボディ（`{ "error": "..." }`）側にある。原因が画面から分かるように拾う。
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const { data } = error as ApiError & { data?: { error?: unknown } };
    if (typeof data?.error === 'string' && data.error) return data.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * APIリクエストを実行する共通関数
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  authHeaders?: Record<string, string>,
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // ヘッダーを明示的に構築
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // 認証ヘッダーを追加
  if (authHeaders) {
    Object.assign(requestHeaders, authHeaders);
  }

  // その他のヘッダーを追加
  if (options.headers) {
    Object.assign(requestHeaders, options.headers);
  }

  // Headersオブジェクトを明示的に使用
  const headersObj = new Headers();

  // ヘッダーを1つずつ設定
  Object.entries(requestHeaders).forEach(([key, value]) => {
    headersObj.set(key, value);
  });

  // 追加の検証：Authorizationヘッダーが存在することを確認
  if (authHeaders && 'Authorization' in authHeaders) {
    // JWTトークンの基本的な構造チェック
    const authValue = authHeaders.Authorization;
    if (authValue.startsWith('Bearer ')) {
      const token = authValue.substring(7); // "Bearer " を除去

      // ローカル開発環境用：空のトークンの場合はスキップ
      if (token.trim() === '') {
        // ローカル開発環境では空のBearerトークンを許可
        if (import.meta.env.VITE_APP_ENV === 'local') {
          // ローカル環境では詳細ログを出力しない
        } else {
          console.warn('⚠️ 空のBearerトークンが送信されました');
        }
      } else {
        const tokenParts = token.split('.');

        // JWTペイロードの基本的なデコード（エラーチェックのみ）
        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          const now = Math.floor(Date.now() / 1000);
          // Token validation without verbose logging
        } catch (jwtError) {
          console.error(
            '🚨 JWT デコードエラー:',
            jwtError instanceof Error ? jwtError.message : 'Unknown error',
          );
        }
      }
    } else {
      console.error('🚨 Authorization ヘッダーの形式が不正:', authValue.substring(0, 20) + '...');
    }
  }

  const requestConfig: RequestInit = {
    method: 'GET', // デフォルトメソッドを明示
    mode: 'cors', // CORSモードを明示
    credentials: 'include', // クロスドメインでも認証情報を送信
    ...options,
    headers: headersObj,
  };

  try {
    const response = await fetch(url, requestConfig);

    // 401エラーの場合、詳細な診断情報を出力
    if (response.status === 401) {
      console.error('🚨 401 Unauthorized - 詳細診断:');
      console.error('🔍 リクエストURL:', response.url);
      console.error('🔍 リクエストタイプ:', response.type);
      console.error(
        '🔍 CORS問題の可能性:',
        response.type === 'opaque' || response.type === 'opaqueredirect',
      );
      console.error('🔍 実際に送信されたヘッダー（推定）:', requestConfig.headers);

      // レスポンスヘッダーから詳細情報を取得
      const corsHeaders = {
        'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
        'access-control-allow-headers': response.headers.get('access-control-allow-headers'),
        'access-control-allow-methods': response.headers.get('access-control-allow-methods'),
        'access-control-allow-credentials': response.headers.get(
          'access-control-allow-credentials',
        ),
      };
      console.error('🔍 サーバーのCORS設定:', corsHeaders);

      // プリフライトリクエストの必要性をチェック
      const needsPreflight = Object.keys(requestConfig.headers || {}).some(
        (header) =>
          !['accept', 'accept-language', 'content-language', 'content-type'].includes(
            header.toLowerCase(),
          ),
      );
      console.error('🔍 プリフライトリクエストが必要:', needsPreflight);

      // エラーレスポンスの内容を取得を試行
      try {
        const errorText = await response.clone().text();
        console.error('🔍 エラーレスポンス内容:', errorText.substring(0, 500));
      } catch (e) {
        console.error(
          '🔍 エラーレスポンス読取り失敗:',
          e instanceof Error ? e.message : 'Unknown error',
        );
      }
    }

    if (!response.ok) {
      // エラーレスポンスのボディを読み込む
      let errorData: any = null;
      try {
        errorData = await response.json();
      } catch (e) {
        // JSONパースに失敗した場合はテキストとして読み込む
        try {
          const errorText = await response.text();
          errorData = { error: errorText };
        } catch (textError) {
          // テキスト読み込みも失敗した場合は空のデータ
          errorData = { error: 'Failed to read error response' };
        }
      }

      const error = new ApiError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
        response,
      );
      // エラーデータをApiErrorオブジェクトに追加
      (error as any).data = errorData;
      throw error;
    }

    // 204 No Content など空レスポンスの場合はJSONパースをスキップ
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('📡 fetch()エラー:', error);

    // ネットワークエラーの詳細分析
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('🚨 ネットワークエラー詳細分析:');
      console.error('🔍 CORS blocking の可能性が高い');
      console.error('🔍 プリフライトリクエストが失敗している可能性');
      console.error('🔍 サーバー側でAuthorizationヘッダーがブロックされている可能性');
    }

    if (error instanceof ApiError) {
      throw error;
    }

    // ネットワークエラーなど
    throw new ApiError(
      `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      0,
    );
  }
}

/**
 * GETリクエスト
 */
export async function apiGet<T>(
  endpoint: string,
  authHeaders?: Record<string, string>,
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' }, authHeaders);
}

/**
 * POSTリクエスト
 */
export async function apiPost<T>(
  endpoint: string,
  data?: unknown,
  authHeaders?: Record<string, string>,
): Promise<T> {
  return apiRequest<T>(
    endpoint,
    {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    },
    authHeaders,
  );
}

/**
 * PUTリクエスト
 */
export async function apiPut<T>(
  endpoint: string,
  data?: unknown,
  authHeaders?: Record<string, string>,
): Promise<T> {
  return apiRequest<T>(
    endpoint,
    {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    },
    authHeaders,
  );
}

/**
 * DELETEリクエスト
 */
export async function apiDelete<T>(
  endpoint: string,
  authHeaders?: Record<string, string>,
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' }, authHeaders);
}

/**
 * PATCHリクエスト
 */
export async function apiPatch<T>(
  endpoint: string,
  data?: unknown,
  authHeaders?: Record<string, string>,
): Promise<T> {
  return apiRequest<T>(
    endpoint,
    {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    },
    authHeaders,
  );
}

/**
 * 認証付きAPIクライアント（React Hooks用）
 */
export class AuthenticatedApiClient {
  private authHeaders: Record<string, string>;

  constructor(authHeaders: Record<string, string>) {
    this.authHeaders = authHeaders;
  }

  async get<T>(endpoint: string): Promise<T> {
    return apiGet<T>(endpoint, this.authHeaders);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiPost<T>(endpoint, data, this.authHeaders);
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiPut<T>(endpoint, data, this.authHeaders);
  }

  async delete<T>(endpoint: string): Promise<T> {
    return apiDelete<T>(endpoint, this.authHeaders);
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return apiPatch<T>(endpoint, data, this.authHeaders);
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${API_BASE_URL}${endpoint}`;

    // ヘッダーを明示的に構築
    const requestHeaders: Record<string, string> = {};

    // 認証ヘッダーを追加
    Object.assign(requestHeaders, this.authHeaders);

    // Headersオブジェクトを明示的に使用
    const headersObj = new Headers();

    // ヘッダーを1つずつ設定
    Object.entries(requestHeaders).forEach(([key, value]) => {
      headersObj.set(key, value);
    });

    const requestConfig: RequestInit = {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: headersObj,
    };

    try {
      const response = await fetch(url, requestConfig);

      if (!response.ok) {
        // エラーレスポンスのボディを読み込む
        let errorData: any = null;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            const errorText = await response.text();
            errorData = { error: errorText };
          } catch (textError) {
            errorData = { error: 'Failed to read error response' };
          }
        }

        const error = new ApiError(
          `API request failed: ${response.status} ${response.statusText}`,
          response.status,
          response,
        );
        (error as any).data = errorData;
        throw error;
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('📡 Blob fetch error:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      // ネットワークエラーなど
      throw new ApiError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
      );
    }
  }
}
